/**
 * Compute the Annual GHG Reduction label for a given carrier, extracting unit/type from parentheses for standard values.
 * @param Carrier The GHG carrier object
 * @returns The label string
 */

type AnnualGHGLabelOptions = {
  isCustomGhgUnit?: boolean;
  isCustomGhgType?: boolean;
};

function extractUnitPart(Carrier: any, unitIsCustom: boolean): string {
  if (unitIsCustom) {
    const customUnit = Carrier.customGHGUnit;
    if (customUnit !== undefined && customUnit !== null && customUnit !== "") {
      return customUnit;
    }
    return Carrier.ghgUnit !== "Custom" ? Carrier.ghgUnit ?? "" : "";
  }
  if (Carrier.ghgUnit && Carrier.ghgUnit !== "Custom") {
    const match = /\(([^)]{1,100})\)/.exec(Carrier.ghgUnit ?? "");
    return match?.[1] ?? "";
  }
  return "";
}

function extractTypePart(Carrier: any, typeIsCustom: boolean): string {
  if (typeIsCustom) {
    const customType = Carrier.customGHGType;
    if (customType !== undefined && customType !== null && customType !== "") {
      return customType;
    }
    return Carrier.ghgType !== "Custom" ? Carrier.ghgType ?? "" : "";
  }
  if (Carrier.ghgType && Carrier.ghgType !== "Custom") {
    const match = /\(([^)]{1,100})\)/.exec(Carrier.ghgType ?? "");
    return match?.[1] ?? "";
  }
  return "";
}

function buildAnnualGHGReductionLabel(unitIsCustom: boolean, typeIsCustom: boolean, Carrier: any, unitPart: string, typePart: string): string {
  if (!typeIsCustom && unitIsCustom && Carrier.ghgType && unitPart) {
    return `Annual GHG Reduction (${Carrier.ghgType}, ${unitPart})`;
  }
  if (!unitIsCustom && !typeIsCustom && unitPart === "" && typePart === "") {
    return "Annual GHG Reduction ()";
  }
  if (unitPart !== "" && typePart !== "") {
    return `Annual GHG Reduction (${unitPart}${typePart})`;
  }
  if (unitPart !== "" && typePart === "") {
    return `Annual GHG Reduction (${unitPart})`;
  }
  if (unitPart === "" && typePart !== "") {
    return `Annual GHG Reduction (${typePart})`;
  }
  return "Annual GHG Reduction";
}

export function getAnnualGHGReductionLabelFromParens(Carrier: any, options: AnnualGHGLabelOptions = {}): string {
  const ghgUnitSet = typeof Carrier.ghgUnit === "string" && Carrier.ghgUnit.trim() !== "";
  const ghgTypeSet = typeof Carrier.ghgType === "string" && Carrier.ghgType.trim() !== "";
  if (!ghgUnitSet || !ghgTypeSet) return "Annual GHG Reduction";

  const unitIsCustom = options.isCustomGhgUnit ?? (Carrier.ghgUnit === "Custom");
  const typeIsCustom = options.isCustomGhgType ?? (Carrier.ghgType === "Custom");
  const unitPart = extractUnitPart(Carrier, unitIsCustom);
  const typePart = extractTypePart(Carrier, typeIsCustom);
  return buildAnnualGHGReductionLabel(unitIsCustom, typeIsCustom, Carrier, unitPart, typePart);
}
// src/pages/ReductionMeasures/CreateUpdateReductionMeasures/ghgLabelHelpers.ts

/**
 * Compute the Annual GHG Reduction label for a given carrier.
 * @param Carrier The GHG carrier object
 * @returns The label string
 */

function extractUnitPartSimple(Carrier: any, unitIsCustom: boolean): string {
  if (unitIsCustom) {
    const customUnit = Carrier.customGHGUnit;
    if (customUnit !== undefined && customUnit !== null && customUnit !== "") {
      return customUnit;
    }
    return Carrier.ghgUnit !== "Custom" ? Carrier.ghgUnit ?? "" : "";
  }
  if (Carrier.ghgUnit && Carrier.ghgUnit !== "Custom") {
    return Carrier.ghgUnit;
  }
  return "";
}

function extractTypePartSimple(Carrier: any, typeIsCustom: boolean): string {
  if (typeIsCustom) {
    const customType = Carrier.customGHGType;
    if (customType !== undefined && customType !== null && customType !== "") {
      return customType;
    }
    return Carrier.ghgType !== "Custom" ? Carrier.ghgType ?? "" : "";
  }
  if (Carrier.ghgType && Carrier.ghgType !== "Custom") {
    return Carrier.ghgType;
  }
  return "";
}

function buildAnnualGHGReductionLabelSimple(unitIsCustom: boolean, typeIsCustom: boolean, Carrier: any, unitPart: string, typePart: string): string {
  if (!typeIsCustom && unitIsCustom && Carrier.ghgType && unitPart) {
    return `Annual GHG Reduction (${Carrier.ghgType}, ${unitPart})`;
  }
  if (typeIsCustom && !unitIsCustom && typePart && unitPart) {
    return `Annual GHG Reduction (${typePart}, ${unitPart})`;
  }
  if (typeIsCustom && unitIsCustom && typePart && unitPart) {
    return `Annual GHG Reduction (${typePart}, ${unitPart})`;
  }
  if (!typeIsCustom && !unitIsCustom && typePart && unitPart) {
    return `Annual GHG Reduction (${unitPart}, ${typePart})`;
  }
  if (!unitIsCustom && !typeIsCustom && unitPart === "" && typePart === "") {
    return "Annual GHG Reduction ()";
  }
  if (unitPart !== "" && typePart === "") {
    return `Annual GHG Reduction (${unitPart})`;
  }
  if (unitPart === "" && typePart !== "") {
    return `Annual GHG Reduction (${typePart})`;
  }
  return "Annual GHG Reduction";
}

export function getAnnualGHGReductionLabel(Carrier: any, options: AnnualGHGLabelOptions = {}): string {
  const ghgUnitSet = typeof Carrier.ghgUnit === "string" && Carrier.ghgUnit.trim() !== "";
  const ghgTypeSet = typeof Carrier.ghgType === "string" && Carrier.ghgType.trim() !== "";
  if (!ghgUnitSet || !ghgTypeSet) return "Annual GHG Reduction";

  const unitIsCustom = options.isCustomGhgUnit ?? (Carrier.ghgUnit === "Custom");
  const typeIsCustom = options.isCustomGhgType ?? (Carrier.ghgType === "Custom");
  const unitPart = extractUnitPartSimple(Carrier, unitIsCustom);
  const typePart = extractTypePartSimple(Carrier, typeIsCustom);
  return buildAnnualGHGReductionLabelSimple(unitIsCustom, typeIsCustom, Carrier, unitPart, typePart);
}
